import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? '/api' : 'http://localhost:3002/api');

// Allow configurable timeout (defaults to 10 minutes for CLI + Gemini analysis)
// Can be overridden with REACT_APP_API_TIMEOUT_MS env var
const API_TIMEOUT_MS = parseInt(process.env.REACT_APP_API_TIMEOUT_MS || '600000', 10);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
});

apiClient.defaults.withCredentials = true;

// Response interceptor to handle 403 errors (permission changes)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If we get a 403 on a protected route, the user's role may have changed
    // Trigger a reload to update the UI
    if (error.response?.status === 403 && 
        error.config?.url?.includes('/config') || 
        error.config?.url?.includes('/llm-config') ||
        error.config?.url?.includes('/company-docs') ||
        error.config?.url?.includes('/users')) {
      console.log('[API] Permission denied - user role may have changed');
      // The component will handle this by showing an error and redirecting
    }
    return Promise.reject(error);
  }
);

export function resolveAuthLoginUrl(redirectPath) {
  const params = new URLSearchParams();
  if (redirectPath && redirectPath.startsWith('/')) {
    params.set('redirect', redirectPath);
  }

  // In development, use absolute URL to backend server for OAuth redirects
  // (window.location navigation doesn't use webpack proxy)
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('http')) {
    const trimmed = process.env.REACT_APP_API_URL.replace(/\/$/, '');
    const base = trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed;
    const query = params.toString();
    return query ? `${base}/api/auth/google?${query}` : `${base}/api/auth/google`;
  }

  // For local development, construct absolute URL to backend
  if (isDevelopment && typeof window !== 'undefined') {
    const backendUrl = 'http://localhost:3002';
    const query = params.toString();
    return query ? `${backendUrl}/api/auth/google?${query}` : `${backendUrl}/api/auth/google`;
  }

  const query = params.toString();
  return query ? `/api/auth/google?${query}` : '/api/auth/google';
}

export function resolveGoogleDocsAuthUrl(redirectPath) {
  const params = new URLSearchParams();
  params.set('include_docs', 'true');
  if (redirectPath && redirectPath.startsWith('/')) {
    params.set('redirect', redirectPath);
  }

  // In development, use absolute URL to backend server for OAuth redirects
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('http')) {
    const trimmed = process.env.REACT_APP_API_URL.replace(/\/$/, '');
    const base = trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed;
    return `${base}/api/auth/google?${params.toString()}`;
  }

  // For local development, construct absolute URL to backend
  if (isDevelopment && typeof window !== 'undefined') {
    const backendUrl = 'http://localhost:3002';
    return `${backendUrl}/api/auth/google?${params.toString()}`;
  }

  return `/api/auth/google?${params.toString()}`;
}

export const authService = {
  getCurrentUser: () => apiClient.get('/auth/me'),
  logout: () => apiClient.post('/auth/logout'),
};

export const projectService = {
  // Get all projects
  getProjects: () => apiClient.get('/projects'),
  
  // Get project by ID
  getProject: (id) => apiClient.get(`/projects/${id}`),
  
  // Create new project
  createProject: (projectData) => apiClient.post('/projects', projectData),
  
  // Update project
  updateProject: (id, projectData) => apiClient.put(`/projects/${id}`, projectData),
  
  // Delete project
  deleteProject: (id) => apiClient.delete(`/projects/${id}`),
};

export const folderService = {
  // Get all folders and projects (organized by shared/personal)
  getFoldersAndProjects: (userId = null) => 
    apiClient.get('/folders', { params: { userId } }),
  
  // Get folder by ID
  getFolder: (id) => apiClient.get(`/folders/${id}`),
  
  // Create new folder
  createFolder: (folderData) => apiClient.post('/folders', folderData),
  
  // Update folder
  updateFolder: (id, folderData) => apiClient.put(`/folders/${id}`, folderData),
  
  // Move folder to new parent
  moveFolder: (id, parentId) => 
    apiClient.put(`/folders/${id}/move`, { parentId }),
  
  // Delete folder
  deleteFolder: (id) => apiClient.delete(`/folders/${id}`),
  
  // Get or create personal workspace
  getPersonalWorkspace: (userId) => 
    apiClient.get(`/folders/personal-workspace/${userId}`),
};

export const processService = {
  // Get all processes for a project
  getProcessesByProject: (projectId) => apiClient.get(`/processes/project/${projectId}`),
  
  // Get process by ID
  getProcess: (id) => apiClient.get(`/processes/${id}`),
  
  // Get files for a process
  getProcessFiles: (processId) => apiClient.get(`/processes/${processId}/files`),
  
  // Get code file content by ID
  getCodeFile: (codeId) => apiClient.get(`/processes/code/${codeId}`),
  
  // Create new process
  createProcess: (processData) => apiClient.post('/processes', processData),
  
  // Update process
  updateProcess: (id, processData) => apiClient.put(`/processes/${id}`, processData),
  
  // Delete process
  deleteProcess: (id) => apiClient.delete(`/processes/${id}`),
};

export const analysisService = {
  // Run code analysis with ZIP file
  analyzeCodeZip: (zipFile, onUploadProgress = null) => {
    const formData = new FormData();
    formData.append('zipFile', zipFile);
    
    console.log(`Starting upload: ${zipFile.name} (${(zipFile.size / 1024 / 1024).toFixed(2)} MB)`);
    const uploadStart = Date.now();
    
    return apiClient.post('/analysis/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Ensure this specific request uses the configured longer timeout
      timeout: API_TIMEOUT_MS,
      // Add upload progress tracking
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        const elapsed = Date.now() - uploadStart;
        const rate = (progressEvent.loaded / elapsed * 1000 / 1024 / 1024).toFixed(2); // MB/s
        
        console.log(`Upload progress: ${progress}% (${rate} MB/s)`);
        
        if (onUploadProgress) {
          onUploadProgress({
            progress,
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            rate,
            elapsed
          });
        }
      },
      // Optimize for large file uploads
      maxContentLength: 200 * 1024 * 1024, // 200MB
      maxBodyLength: 200 * 1024 * 1024, // 200MB
    }).then(response => {
      const totalTime = Date.now() - uploadStart;
      console.log(`Upload completed in ${(totalTime / 1000).toFixed(1)}s`);
      return response;
    });
  },
  
  // Run code analysis (legacy)
  analyzeCode: (projectId, analysisData, zipFile) => {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('analysisData', JSON.stringify(analysisData));
    if (zipFile) {
      formData.append('zipFile', zipFile);
    }
    
    return apiClient.post('/analysis/code', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Run execution analysis
  analyzeExecution: (projectId, logFiles, recordingFile) => {
    const formData = new FormData();
    formData.append('projectId', projectId);
    
    if (logFiles) {
      Array.from(logFiles).forEach(file => {
        formData.append('logFiles', file);
      });
    }
    
    if (recordingFile) {
      formData.append('recordingFile', recordingFile);
    }
    
    return apiClient.post('/analysis/execution', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Export analysis to Google Docs
  exportToGoogleDocs: (analysisId) => 
    apiClient.post(`/analysis/export-to-docs/${analysisId}`),
};

export const chatService = {
  // Get all conversations for a project
  getConversations: (projectId, isCrossProject = false) =>
    apiClient.get('/chat/conversations', {
      params: {
        projectId: isCrossProject ? null : projectId,
        isCrossProject
      }
    }),

  // Create a new conversation
  createConversation: (projectId, isCrossProject = false) =>
    apiClient.post('/chat/conversations', {
      projectId: isCrossProject ? null : projectId,
      isCrossProject
    }),

  // Get messages for a specific conversation
  getConversationMessages: (conversationId) =>
    apiClient.get(`/chat/conversations/${conversationId}`),

  // Delete a conversation
  deleteConversation: (conversationId) =>
    apiClient.delete(`/chat/conversations/${conversationId}`),

  // Send chat message
  sendMessage: (projectId, message, conversationId = null, isCrossProject = false, processId = null, selectedProjectIds = null) => 
    apiClient.post('/chat/message', { 
      projectId: isCrossProject ? null : projectId, 
      message,
      conversationId,
      isCrossProject,
      processId: processId || null,
      selectedProjectIds: selectedProjectIds || null
    }),
  
  // Legacy: Get chat history (deprecated)
  getChatHistory: (projectId, isCrossProject = false) => 
    apiClient.get('/chat/history', { 
      params: { 
        projectId: isCrossProject ? null : projectId, 
        isCrossProject 
      } 
    }),
  
  // Clear all conversations for a project
  clearChatHistory: (projectId, isCrossProject = false) =>
    apiClient.delete('/chat/history', {
      params: {
        projectId: isCrossProject ? null : projectId,
        isCrossProject
      }
    }),

  // Run code analysis in chat context
  analyzeCode: (projectId, processId, scope, files = [], conversationId = null) =>
    apiClient.post('/chat/analyze-code', {
      projectId,
      processId,
      scope,
      files,
      conversationId
    }),

  // Export chat message to Google Docs
  exportMessageToGoogleDocs: (messageId, content, projectName = null) =>
    apiClient.post('/chat/export-to-docs', { messageId, content, projectName }),
};

export const configService = {
  // Get configuration settings
  getConfiguration: () => apiClient.get('/config'),
  
  // Save configuration settings
  saveConfiguration: (config) => apiClient.post('/config', config),
  
  // Get list of available prompts
  getPrompts: () => apiClient.get('/config/prompts'),
  
  // Get content of a specific prompt
  getPromptContent: (promptName) => apiClient.get(`/config/prompts/${promptName}`),
  
  // Save prompt content
  savePrompt: (promptName, content) => 
    apiClient.post(`/config/prompts/${promptName}`, { content }),
};

export const llmConfigService = {
  // Get all available LLM models
  getAvailableModels: (includeInactive = false) => 
    apiClient.get('/llm-config/models', { params: { all: includeInactive } }),
  
  // Create a new LLM model
  createModel: (modelData) => apiClient.post('/llm-config/models', modelData),
  
  // Update an existing LLM model
  updateModel: (modelId, modelData) => apiClient.put(`/llm-config/models/${modelId}`, modelData),
  
  // Delete an LLM model
  deleteModel: (modelId) => apiClient.delete(`/llm-config/models/${modelId}`),
  
  // Get LLM preference for a project or global default
  getPreference: (projectId = null) => 
    apiClient.get('/llm-config/preference', { params: { projectId } }),
  
  // Set LLM preference
  setPreference: (modelName, temperature, projectId = null) => {
    const body = { modelName, temperature };
    if (projectId !== null && projectId !== undefined) {
      body.projectId = projectId;
    }
    return apiClient.post('/llm-config/preference', body);
  },
  
  // Delete preference (revert to default)
  deletePreference: (projectId = null) =>
    apiClient.delete('/llm-config/preference', { params: { projectId } }),
};

export const companyDocumentService = {
  // List all indexed company documents
  listDocuments: () => apiClient.get('/company-docs'),
  
  // Ingest a new document from Confluence URL
  ingestDocument: (url) => apiClient.post('/company-docs/ingest', { url }),
  
  // Delete a document and all its chunks
  deleteDocument: (url) => apiClient.delete('/company-docs', { params: { url } }),
  
  // Search for similar documents
  searchDocuments: (query, limit = 5) => 
    apiClient.post('/company-docs/search', { query, limit }),
};

export const userService = {
  // Get all users (admin only)
  getAllUsers: () => apiClient.get('/users'),
  
  // Update user role (admin only)
  updateUserRole: (userId, role) => 
    apiClient.patch(`/users/${userId}/role`, { role }),
};

export default apiClient;